import type { SegmentTrackCartViewedEvent } from "@beautypie/segment/Track/CartViewed"
import type { SegmentTrackCheckoutStartedEvent } from "@beautypie/segment/Track/CheckoutStarted"
import type { SegmentTrackCheckoutStepViewedEvent } from "@beautypie/segment/Track/CheckoutStepViewed"
import {
  formatMembershipDataFromMembershipType,
  formatProductDataFromVariantType,
  getCurrency,
  notEmpty,
  ProductPriceType,
} from "data/tracking/segment/helpers"
import { SegmentCheckoutEventType } from "data/tracking/segment/helpers.types"
import {
  useAppContext,
  useBasketBuckets,
  useDeliveryType,
  useLocale,
  useUserV2,
} from "hooks"
import { useBasket } from "lib/basket"
import { userVar, useUser } from "lib/user"
import { useCallback, useMemo, useRef } from "react"
import { trackClick, useSegment } from "tracking"
import { getProductsFromInLimitItems } from "tracking/utils/inLineItems"
import type {
  BasketPricingType,
  IncludedDiscountType,
  ItemType,
  RejectedDiscountType,
} from "types"

export enum CheckoutSteps {
  BASKET = 1,
  REGISTER = 2,
  CHECKOUT = 3,
  CONFIRMATION = 4,
}

type DiscountType = "Included" | "Rejected"

type BasketTrackingHook = {
  trackCartViewed: () => void
  trackStepViewed: (step: CheckoutSteps) => void
  trackStepCompleted: (
    step: CheckoutSteps,
    overrides?: Record<string, unknown>
  ) => void
  trackStepCompletedInstantly: (
    step: CheckoutSteps,
    overrides?: Record<string, unknown>
  ) => void
  trackCheckoutStarted: (props?: {
    checkout_type?: "apple_pay_express" | "standard"
    instantTrack?: boolean
  }) => void
}

const calculateDiscount = (pricing: BasketPricingType | undefined) =>
  pricing?.totalDiscountsByType.reduce((acc, curr) => acc + curr.value, 0) || 0

const formatProducts = (
  items: ItemType[],
  locale: string,
  hasMembership: boolean
) => {
  return items
    .map((item, index) => {
      const { quantity, variant, membership } = item
      const properties = { quantity, position: index + 1 }

      if (variant) {
        return {
          ...properties,
          ...formatProductDataFromVariantType(
            variant,
            locale,
            hasMembership
              ? ProductPriceType.MemberPrice
              : ProductPriceType.TypicalPrice
          ),
          ...(item.origin === "discount" ? { price: 0.0 } : {}),
        }
      }

      if (membership) {
        return {
          ...properties,
          ...formatMembershipDataFromMembershipType(membership),
        }
      }

      return undefined
    })
    .filter(notEmpty)
}

const getCoupons = (
  includedDiscounts: IncludedDiscountType[],
  rejectedDiscounts: RejectedDiscountType[]
) =>
  includedDiscounts?.length > 0 || rejectedDiscounts?.length > 0
    ? [...includedDiscounts, ...rejectedDiscounts].reduce(
        (acc: { id: string; discount_type: DiscountType }[], obj) => {
          if (obj?.code) {
            if (includedDiscounts.includes(obj as IncludedDiscountType)) {
              acc.push({
                id: obj.code,
                discount_type: "Included",
              })
            } else {
              acc.push({
                id: obj.code,
                discount_type: "Rejected",
              })
            }
          }
          return acc
        },
        []
      )
    : []

const useCartViewedData = (): SegmentTrackCartViewedEvent => {
  const { currency } = useAppContext()
  const { hasMembership } = useUser()
  const basket = useBasket()
  const locale = useLocale()
  const { inLimitItems, memberships } = useBasketBuckets()

  const isGwpEligible = useMemo(() => {
    return !!basket.gwpModule?.items && basket.gwpModule.items.length > 0
  }, [basket])

  return useMemo(() => {
    const itemsWithMembership = inLimitItems.filter(
      (item) => item.variant?.brandName === "Member"
    )
      ? inLimitItems
      : [...inLimitItems, ...memberships]

    return {
      event: "Cart Viewed",
      properties: {
        products: getProductsFromInLimitItems(
          itemsWithMembership,
          hasMembership,
          locale
        ),
        product_price_type: hasMembership ? "Member Price" : "Typical Price",
        currency,
        is_eligible_gwp: isGwpEligible,
      },
    }
  }, [
    inLimitItems,
    memberships,
    hasMembership,
    locale,
    currency,
    isGwpEligible,
  ])
}

const useCheckoutStepData =
  (): SegmentTrackCheckoutStepViewedEvent["properties"] => {
    const locale = useLocale()
    const { inLimitItems: items } = useBasketBuckets()
    const shippingMethod = useDeliveryType()
    const { hasMembership, pricing } = useUser()

    const purchaseType = hasMembership
      ? ProductPriceType.MemberPrice
      : ProductPriceType.TypicalPrice

    const discount = calculateDiscount(pricing!)
    const products = formatProducts(items, locale, hasMembership)
    return {
      step: 1 as const,
      shipping_method: shippingMethod,
      payment_method: "",
      currency: getCurrency(locale),
      revenue: pricing?.totalPrice.gross || 0,
      discount,
      shipping: pricing?.shipping.gross || pricing?.estimatedShipping || 0,
      product_price_type: purchaseType,
      products,
    }
  }

export const useCheckoutStartedData =
  (): SegmentTrackCheckoutStartedEvent["properties"] => {
    const { inLimitItems: items } = useBasketBuckets()
    const locale = useLocale()
    const { hasMembership, includedDiscounts, rejectedDiscounts, pricing } =
      useUserV2()

    const discount = calculateDiscount(pricing!)
    const products = formatProducts(items, locale, hasMembership)
    const coupons = getCoupons(includedDiscounts, rejectedDiscounts)

    return {
      checkout_type: "standard" as "standard" | "apple_pay_express",
      coupons,
      currency: getCurrency(locale),
      revenue: pricing?.totalPrice.gross || 0,
      discount,
      shipping: pricing?.shipping.gross || pricing?.estimatedShipping || 0,
      product_price_type: hasMembership
        ? ProductPriceType.MemberPrice
        : ProductPriceType.TypicalPrice,
      products,
    }
  }

export const useBasketTracking = (): BasketTrackingHook => {
  const cartViewedData = useCartViewedData()
  const checkoutStepData = useCheckoutStepData()
  const checkoutStartedData = useCheckoutStartedData()

  const { track } = useSegment()

  // TODO - is there a better way to do exactly once tracking?
  const trackingState = useRef({
    cartViewed: false,
    stepViewed: false,
    stepCompleted: false,
  })

  const trackCartViewed = useCallback(() => {
    if (trackingState.current.cartViewed) return

    track(cartViewedData)

    trackingState.current = { ...trackingState.current, cartViewed: true }
  }, [cartViewedData, track])

  const trackStepViewed = useCallback(
    (step: CheckoutSteps) => {
      if (trackingState.current.stepViewed) return

      track({
        event: SegmentCheckoutEventType.Viewed,
        properties: { ...checkoutStepData, step },
      })

      trackingState.current = { ...trackingState.current, stepViewed: true }
    },
    [track, checkoutStepData]
  )

  const trackStepCompleted = useCallback(
    (step: CheckoutSteps, overrides: Record<string, unknown> = {}) => {
      if (trackingState.current.stepCompleted) return

      trackClick({
        event: SegmentCheckoutEventType.Completed,
        properties: { ...checkoutStepData, step, ...overrides },
      })

      trackingState.current = { ...trackingState.current, stepCompleted: true }
    },
    [checkoutStepData]
  )

  //The reason that this is created is cause 'trackStepCompleted' is waiting the url path to be changed on safari
  const trackStepCompletedInstantly = useCallback(
    (step: CheckoutSteps, overrides: Record<string, unknown> = {}) => {
      if (trackingState.current.stepCompleted) return

      track({
        event: SegmentCheckoutEventType.Completed,
        properties: { ...checkoutStepData, step, ...overrides },
      })

      trackingState.current = { ...trackingState.current, stepCompleted: true }
    },
    [track, checkoutStepData]
  )

  const trackCheckoutStarted = useCallback(
    ({
      checkout_type = "standard",
      instantTrack = false,
    }: {
      checkout_type?: "standard" | "apple_pay_express"
      instantTrack?: boolean
    } = {}) => {
      const trackMethod = instantTrack ? track : trackClick

      trackMethod({
        event: "Checkout Started",
        properties: { ...checkoutStartedData, checkout_type },
      })
    },
    [track, checkoutStartedData]
  )

  return {
    trackCartViewed,
    trackStepViewed,
    trackStepCompleted,
    trackStepCompletedInstantly,
    trackCheckoutStarted,
  }
}

type PromoCodeTracking = () => {
  trackPromoCodeEntered: (code: string) => void
  trackPromoCodeApplied: (code: string, name: string, value: number) => void
  trackPromoCodeRejected: (code: string, state: string) => void
  trackPromoCodeRemoved: (code: string) => void
}

export const usePromoCodeTracking: PromoCodeTracking = () => {
  const { track } = useSegment()
  const { includedDiscounts = [] } = userVar()

  return {
    trackPromoCodeEntered: (code: string) => {
      track({
        event: "Coupon Entered",
        properties: {
          coupon_id: code,
        },
      })
    },
    trackPromoCodeApplied: (code: string, name: string, value: number) => {
      track({
        event: "Coupon Applied",
        properties: {
          coupon_id: code,
          coupon_name: name,
          discount: value,
        },
      })
    },
    trackPromoCodeRejected: (code: string, state: string) => {
      track({
        event: "Coupon Denied",
        properties: {
          coupon_id: code,
          reason: state,
        },
      })
    },
    trackPromoCodeRemoved: (code: string) => {
      // @TODO it would be really nice not to have to couple this to the global state
      const { name, value } = includedDiscounts.find(
        ({ code: includedCode }) => includedCode === code
      ) || { name: "unknown", value: 0 }

      track({
        event: "Coupon Removed",
        properties: {
          coupon_id: code,
          coupon_name: name,
          discount: value,
        },
      })
    },
  }
}
